import { Grid } from '@mui/material';
import { Trans } from 'react-i18next';

import {
  InfoReviewFieldLabel,
  InfoReviewFieldValue,
} from '@/components/Form/Form.styled';

import { EMPLOYMENT_STATUS_TEXT, PROFESSION_TEXT } from '@/constants/user';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';
import { selectUser } from '@/redux/user-data/selectors';

import { EmploymentDetails, EmploymentStatus, User } from '@/types/user';

import { formatDateValue, getDisplayedPhone } from '@/utils/string';
import { getOccupationName } from '@/utils/user';

import { ReviewCard } from './ReviewCard';
import { SectionProps } from './types';

const FIELD_PREFIX = 'moreInfo.reviewMyInformation.employmentDetails.field';

export function EmploymentDetailsSection({
  currentIndex,
  totalSections,
}: Readonly<SectionProps>) {
  const user = useAppSelector(selectUser);
  const { t } = useTranslate();

  const employment = user?.employmentDetailed;

  return (
    <ReviewCard
      title={t('moreInfo.reviewMyInformation.employmentDetails.title')}
      currentIndex={currentIndex}
      totalSections={totalSections}
    >
      <EmploymentDetailsSectionContent employment={employment} user={user} />
    </ReviewCard>
  );
}

type EmploymentDetailsSectionContentProps = {
  employment?: EmploymentDetails;
  user?: User;
};

function EmploymentDetailsSectionContent({
  employment,
  user,
}: Readonly<EmploymentDetailsSectionContentProps>) {
  switch (employment?.employmentStatus) {
    case EmploymentStatus.Employed:
      return <EmployedInfo employment={employment} user={user} />;
    case EmploymentStatus.SelfEmployed:
      return <SelfEmployedInfo employment={employment} />;
    case EmploymentStatus.Unemployed:
      return <UnemployedInfo employment={employment} />;
    default:
      return null;
  }
}

type EmploymentInfoProps = {
  employment: EmploymentDetails;
  user?: User;
};

function EmployedInfo({ employment, user }: Readonly<EmploymentInfoProps>) {
  const { t, country } = useTranslate();
  const { occupations, directorOccupations, dateFormat } =
    useAppSelector(selectConfigsState);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.employmentStatus`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {t(EMPLOYMENT_STATUS_TEXT[EmploymentStatus.Employed])}
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.employmentType`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {employment.employmentDesc
              ? t(PROFESSION_TEXT[employment.employmentDesc])
              : ''}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.employerName`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>{employment.employerName}</InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.occupation`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {getOccupationName({ user, occupations, directorOccupations })}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>
      <InfoReviewFieldLabel>
        {t(`${FIELD_PREFIX}.employerAddress`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>{employment.employerAddress}</InfoReviewFieldValue>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.countryOfEmployment`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {employment.employerCountry &&
              (country[employment.employerCountry] ||
                employment.employerCountry)}
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.employerPhone`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {getDisplayedPhone(employment.employerPhone)}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>
      <InfoReviewFieldLabel>
        {t(`${FIELD_PREFIX}.startDate`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {employment.startDate
          ? formatDateValue(employment.startDate, dateFormat)
          : ''}
      </InfoReviewFieldValue>
      <InfoReviewFieldLabel>
        <Trans
          i18nKey={`${FIELD_PREFIX}.riskSector`}
          components={{
            ul: <ul style={{ margin: 0, paddingLeft: 20 }} />,
            li: <li />,
          }}
        />
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {employment.riskSectorFlag ? t('common.yes') : t('common.no')}
      </InfoReviewFieldValue>
    </>
  );
}

function SelfEmployedInfo({ employment }: Readonly<EmploymentInfoProps>) {
  const { t, country } = useTranslate();

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.employmentStatus`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {t(EMPLOYMENT_STATUS_TEXT[EmploymentStatus.SelfEmployed])}
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.selfEmploymentType`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {employment.employmentDesc
              ? t(PROFESSION_TEXT[employment.employmentDesc])
              : ''}
          </InfoReviewFieldValue>
        </Grid>
      </Grid>
      <InfoReviewFieldLabel>
        <Trans
          i18nKey={`${FIELD_PREFIX}.riskSector`}
          components={{
            ul: <ul style={{ margin: 0, paddingLeft: 20 }} />,
            li: <li />,
          }}
        />
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {employment.riskSectorFlag ? t('common.yes') : t('common.no')}
      </InfoReviewFieldValue>
      <InfoReviewFieldLabel>
        {t(`${FIELD_PREFIX}.startupStatus`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {employment.isStartup ? t('common.yes') : t('common.no')}
      </InfoReviewFieldValue>
      <InfoReviewFieldLabel>
        {t(`${FIELD_PREFIX}.countryOfBusiness`)}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {employment.employerCountry &&
          (country[employment.employerCountry] || employment.employerCountry)}
      </InfoReviewFieldValue>
    </>
  );
}

function UnemployedInfo({ employment }: Readonly<EmploymentInfoProps>) {
  const { t } = useTranslate();

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <InfoReviewFieldLabel>
          {t(`${FIELD_PREFIX}.employmentStatus`)}
        </InfoReviewFieldLabel>
        <InfoReviewFieldValue>
          {t(EMPLOYMENT_STATUS_TEXT[EmploymentStatus.Unemployed])}
        </InfoReviewFieldValue>
      </Grid>
      <Grid item xs={6}>
        <InfoReviewFieldLabel>
          {t(`${FIELD_PREFIX}.unemploymentType`)}
        </InfoReviewFieldLabel>
        <InfoReviewFieldValue>
          {employment.employmentDesc
            ? t(PROFESSION_TEXT[employment.employmentDesc])
            : ''}
        </InfoReviewFieldValue>
      </Grid>
    </Grid>
  );
}
